import { useCallback, useEffect, useState } from 'react'
import type { PartnerTicket, PartnerTicketCategory } from '../types/partnerTicket'
import { createPartnerTicket, listPartnerTickets } from '../services/partnerTicketsService'
import { useAuth } from '../contexts/AuthContext'

export function usePartnerTickets() {
  const { user, claims } = useAuth()
  const [tickets, setTickets] = useState<PartnerTicket[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTickets = useCallback(async () => {
    if (!user || claims?.role !== 'partner') return
    setLoading(true)
    setError(null)
    try {
      const list = await listPartnerTickets(50)
      setTickets(list)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar chamados.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [user, claims])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  const create = useCallback(
    async (input: {
      category: PartnerTicketCategory
      subject: string
      description: string
      relatedTaskId?: string
    }) => {
      if (!user || claims?.role !== 'partner') return
      setCreating(true)
      setError(null)
      try {
        await createPartnerTicket(input)
        await fetchTickets()
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Erro ao abrir chamado.'
        setError(msg)
        throw err
      } finally {
        setCreating(false)
      }
    },
    [user, claims, fetchTickets]
  )

  return { tickets, loading, creating, error, refetch: fetchTickets, createTicket: create }
}
